import { Injectable } from "@angular/core";
import { NotificationsService, NotificationType } from "angular2-notifications";
import {
  BehaviorSubject,
  Observable,
  debounce,
  filter,
  forkJoin,
  interval,
  of,
  switchMap,
  tap,
} from "rxjs";
import { apiCallWrapper, queryToParams } from "../api/api.util";
import { CartApi } from "../api/cart.api";
import { IQueryFilter, QueryResult } from "../model/query.filter.class";
import {
  CartAttrs,
  IBusinessUnitChartResponse,
  ICartList,
  IOrderChartResponse,
  IVisitorResponse,
  NewCartItem,
  Order,
  ServerCartItem,
} from "../model/cart.model";
import { HasId } from "../model/generics";
import { UnleashedCustomerExtended } from "../model/unleashed.model";
import { SecurityService } from "./security.service";
import { Cart } from "../model/cart.model";
import * as exactMath from "exact-math";
import { SessionApi } from "../api/session.api";
import { IOrderCardPaymentPrepared } from "../model/order.model";
import { CollectionService } from "./collection.service";
import { LoadingService } from "./core/loading.service";
import { IAuthCustomerUserAllocationCollection } from "../model/customer.user.model";
import { IShortAllocationCollection } from "../model/collection.model";

@Injectable()
export class CartService {
  public items: ServerCartItem[] = [];
  public cart: Cart = new Cart();
  private ccUpdateRequest$ = new BehaviorSubject<null>(null);
  public cardPaymentDetails$ =
    new BehaviorSubject<IOrderCardPaymentPrepared | null>(null);
  private orderIds: (string | undefined)[] = [];
  showPrice = this.securityService.showPriceInAllocationSearch();

  public miniCart$ = new BehaviorSubject<ServerCartItem | null>(null);

  constructor(
    private notifications: NotificationsService,
    private cartApi: CartApi,
    private securityService: SecurityService,
    private session: SessionApi,
    private collectionService: CollectionService,
    private loaderService: LoadingService,
  ) {
    this.handleCCUpdateRequests();
  }

  setOrderIds(ids: (string | undefined)[]) {
    this.orderIds = ids;
  }

  getOrderIds(): (string | undefined)[] {
    return this.orderIds;
  }

  public getOrderAttrsList(query: IQueryFilter): Observable<ICartList> {
    let params = queryToParams(query);

    return this.loaderService.blockWithLoadingOverlayRx(
      this.cartApi.getOrderAttrsList(params),
    );
  }

  public get(id: number): Observable<Order> {
    return apiCallWrapper(this.cartApi.getOrder(id), {
      notificationsService: this.notifications,
      action: "Fetching Specific Order",
    });
  }

  public updateStatus(id: number): Observable<CartAttrs & HasId> {
    return apiCallWrapper(
      this.loaderService.blockWithLoadingOverlayRx(this.cartApi.put(id)),
      {
        notificationsService: this.notifications,
        action: "Updating Status",
      },
    );
  }

  public sendOrderEmail(id: number) {
    return apiCallWrapper(
      this.loaderService.blockWithLoadingOverlayRx(
        this.cartApi.sendOrderEmail(id),
      ),
      {
        notificationsService: this.notifications,
        action: "Re-Sending Order Confirmation Email",
      },
    );
  }

  public sendOrderApprovalEmail(id: number) {
    return apiCallWrapper(
      this.loaderService.blockWithLoadingOverlayRx(
        this.cartApi.sendOrderApprovalEmail(id),
      ),
      {
        notificationsService: this.notifications,
        action: "Re-Sending Order Approval Email",
      },
    );
  }

  public approveCart(
    id: number,
    secret?: string,
    isPunchout = false,
    approveOnlyAllocatedItem = false,
    items?: Partial<ServerCartItem>[],
  ) {
    return apiCallWrapper(
      this.loaderService.blockWithLoadingOverlayRx(
        this.cartApi.approveCart(
          id,
          secret,
          isPunchout,
          approveOnlyAllocatedItem,
          items,
        ),
      ),
      {
        notificationsService: this.notifications,
        action: "Your order has been sent for Approval",
      },
    );
  }

  public cancel(
    type: string,
    cartId: number,
    secret?: string,
    cancelReason?: string,
  ) {
    return apiCallWrapper(
      this.loaderService.blockWithLoadingOverlayRx(
        this.cartApi.cancel(type, cartId, secret, cancelReason),
      ),
      {
        notificationsService: this.notifications,
        action: `Order ${type == "reject" ? "Rejected" : "Deleted"}`,
      },
    );
  }

  public getCartItems(customerId: number) {
    return this.cartApi
      .getCurrentCartItems(customerId)
      .pipe(tap((items) => (this.items = items)));
  }

  /**
   * @description Gets the current cart within the given customer for the current user.
   * Protected against multiple calls with the same arguments
   *
   * @returns {Promise<Cart>}
   */
  public getUserCart(
    dieSilently: boolean = false,
  ): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      const hasAccess = this.securityService.hasCartAccessRightNow();

      if (!hasAccess) {
        resolve();
        return;
      }

      const customer = this.session.$customerData.getValue();

      if (!customer) {
        if (!dieSilently) {
          const msg =
            "Expected to find customer for current user in getUserCart";
          return reject(msg);
        }
      }

      if (customer) {
        this.cartApi.getCurrentCartForUser(customer.id).subscribe((cart) => {
          this.cart = cart;

          resolve();
        });
      }
    });
  }

  /**
   * @description Appends a cartItem to the current user cart
   *
   * @param {NewCartItem} cartItem
   */
  public addItemToCart(cartItem: NewCartItem) {
    const customer = this.session.$customerData.getValue();
    if (!customer) {
      return;
    }

    return this.cartApi
      .addItemToCurrentCart(customer.id, cartItem)
      .pipe((result) => {
        console.log(result);

        this.cart.itemCount++;
        return result;
      });
  }

  /**
   * @description Updates a server-side cart item
   *
   * @param {NewCartItem} cartItem
   */
  public updateCartItem(customerId: number, cartItem: NewCartItem) {
    return this.cartApi.updateCartItem(customerId, cartItem);
  }

  /**
   * @description removes a cart item from the users current cart
   *
   * @param cartItemId
   */
  private lastDeletedCartItem: ServerCartItem | null = null;

  public deleteCartItem(customerId: number, cartItemId: number) {
    const deletingItem = this.items.find((i) => i.id === cartItemId);
    this.lastDeletedCartItem = deletingItem ? { ...deletingItem } : null;

    return this.loaderService
      .blockWithLoadingOverlayRx(
        this.cartApi.deleteCartItem(customerId, cartItemId),
      )
      .pipe(
        switchMap(() => {
          this.removeCartItemById(cartItemId);
          return this.updateCollectionAllocation(customerId);
        }),
      );
  }

  private updateCollectionAllocation(customerId: number) {
    const deletingItem = this.lastDeletedCartItem;
    if (!deletingItem) return of(null);

    if (!deletingItem.allocationCollectionId) return of(null);

    return this.collectionService.getCollections().pipe(
      switchMap((collections) => {
        const matchingCollection = collections.find((collection) =>
          (collection.allocationAvailableDistribution ?? []).some(
            (a) => a.id === deletingItem.allocationCollectionId,
          ),
        );

        if (!matchingCollection) return of(null);

        const matchingAllocation =
          matchingCollection.allocationAvailableDistribution?.find(
            (a) => a.id === deletingItem.allocationCollectionId,
          );

        let remainingAllocation =
          (matchingAllocation?.count || 0) +
          (deletingItem.allocationCollectionConsumption || 0);

        const productIdSet = new Set(
          matchingCollection.products.map((p) => p.productId),
        );

        const eligibleItems = this.items.filter((item) => {
          if (item.id === deletingItem.id) return false;
          if (!productIdSet.has(item.productId || 0)) return false;

          const consumed = item.allocationCollectionConsumption || 0;
          const qty = item.quantity || 0;

          return !item.allocationCollectionId || consumed < qty;
        });

        if (!eligibleItems.length) return of(null);

        const updateCalls: Observable<{ success: true }>[] = [];

        for (const item of eligibleItems) {
          if (remainingAllocation <= 0) break;

          const qty = item.quantity || 0;
          const consume = Math.min(qty, remainingAllocation);

          item.allocationCollectionId = deletingItem.allocationCollectionId;
          item.allocationCollectionConsumption = consume;
          item.allocationCollectionName = deletingItem.allocationCollectionName;

          remainingAllocation -= consume;

          updateCalls.push(
            this.cartApi.updateCartItem(customerId, item.asNewCartItem(), true),
          );
        }

        this.lastDeletedCartItem = null;

        return updateCalls.length ? forkJoin(updateCalls) : of(null);
      }),
    );
  }

  /**
   * @description Deletes the passed item from the list of cart items
   */
  public removeCartItemById = (itemId: number) => {
    for (let i = 0; i < this.items.length; i++) {
      if (this.items[i].id === itemId) {
        this.items.splice(i, 1);
        this.cart.itemCount--;
        this.collectionService.refreshCollections();
        break;
      }
    }
  };

  /**
   * @description Instantly calculates the total for the cart for display instead of waiting on serverside
   *
   * @returns {number}
   */
  public getSubtotal = (ignoreAllocation: boolean = false) =>
    this.items.reduce(
      (accumulator, val) =>
        exactMath.add(
          accumulator,
          val.asNewCartItem().getTotalPrice(ignoreAllocation),
        ),
      0,
    );

  /**
   * @description Instantly calculates the subsidy total for the cart for display instead of waiting on serverside
   *
   * @returns {number}
   */
  public getSubsidyTotal = (ignoreAllocation: boolean = false) =>
    this.items.reduce(
      (accumulator, val) =>
        exactMath.add(
          accumulator,
          val.asNewCartItem().getTotalSubsidy(ignoreAllocation),
        ),
      0,
    );

  /**
   * @description Instantly calculates the total for the cart for display instead of waiting on serverside
   *
   * @returns {number}
   */
  public getGSTSubtotal = (ignoreAllocation: boolean = false) =>
    this.items
      .filter((item) => item.product.canChargeTax)
      .reduce(
        (accumulator, val) =>
          exactMath.add(
            accumulator,
            val.asNewCartItem().getTotalPrice(ignoreAllocation),
          ),
        0,
      );

  /**
   * @description Instantly calculates the total for the user for display instead of waiting on serverside
   *
   * @returns {number}
   */
  public getUserSubtotal = () =>
    this.items.reduce(
      (accumulator, val) =>
        exactMath.add(accumulator, val.asNewCartItem().getUserPrice()),
      0,
    );

  /**
   * @description Instantly calculates the total for the user for display instead of waiting on serverside
   *
   * @returns {number}
   */
  public getAccountSubtotal = () =>
    this.items.reduce(
      (accumulator, val) =>
        exactMath.add(accumulator, val.asNewCartItem().getAccountPrice()),
      0,
    );

  /**
   * @description provides a consistent calculation of the  Small Order Fee on the current cart
   * @returns {number}
   */
  public getHandlingFee = () => {
    let ignoreAllocation = this.showPrice;

    const customerData = this.session.$customerData.getValue();
    if (!customerData) {
      return 20;
    }
    // Customer has  Small Order Fee disabled
    if (
      typeof customerData.handlingFee === "boolean" &&
      !customerData.handlingFee
    ) {
      return 0;
    }

    if (customerData.salesOrderGroup == 'Core') {
      return 20;
    }

    if (
      this.cart.attrs.shippingDetails &&
      this.cart.attrs.shippingDetails.isFreeHandling
    ) {
      return 0;
    }
    // Handle custom  Small Order Fee
    const handlingFeeAmount = customerData.handlingFeeAmount || 20;

    // Do not compute further if there is no fee to calculate
    if (handlingFeeAmount === 0) {
      return handlingFeeAmount;
    }

    // Handle custom  Small Order Fee threshold
    const handlingFeeThreshold =
      !customerData.handlingFeeThreshold ||
        customerData.handlingFeeThreshold <= 0
        ? 250
        : customerData.handlingFeeThreshold;

    if (this.getSubtotal(ignoreAllocation) == 0) {
      return 0;
    }

    if (this.getSubtotal(ignoreAllocation) > handlingFeeThreshold) {
      return 0;
    }

    // Handle CoPay by the customer
    const handlingFeeCoPay =
      !customerData.handlingFeeCoPay || customerData.handlingFeeCoPay <= 0
        ? 0
        : customerData.handlingFeeCoPay;

    if (handlingFeeCoPay > handlingFeeAmount) {
      return 0;
    }

    // Handle Every item having an Allocation
    if (!ignoreAllocation && this.isFullyAllocated()) {
      return 0;
    }

    return exactMath.sub(handlingFeeAmount, handlingFeeCoPay);
  };

  //   /**
  //  * @description provides a consistent calculation of the  Small Order Fee on the current cart
  //  * @returns {number}
  //  */
  //   public getShippingFee = (ignoreAllocation = this.showPrice) => {
  //     const customerData = this.session.$customerData.getValue();
  //     if (!customerData || !customerData.) {
  //       return 0;
  //     }
  //     if (!ignoreAllocation && this.isFullyAllocated()) {
  //       return 0;
  //     }
  //     // if (customerData.salesOrderGroup == 'Core') {
  //       return 20;
  //     // } else {
  //     //   const handlingFeeThreshold =
  //     //     !customerData.handlingFeeThreshold ||
  //     //       customerData.handlingFeeThreshold <= 0
  //     //       ? 250
  //     //       : customerData.handlingFeeThreshold;
  //     //   if (this.getSubtotal(true) > handlingFeeThreshold) {
  //     //     return 0;
  //     //   }

  //     //   return 20;
  //     // }
  //   };

  // public canShowflateShippingFee() {
  //   const customerData = this.session.$customerData.getValue();
  //   if (!customerData || !customerData.) {
  //     return false;
  //   }
  //   return true;
  // }

  public getTax = () => {
    const baseAmount = exactMath.add(
      this.getGSTSubtotal(this.showPrice),
      this.getHandlingFee(),
      // this.getShippingFee(false)
    );

    const gst = this.securityService.getCustomerTax();

    return gst > 0 ? exactMath.mul(baseAmount, gst) : 0;
  };

  public getGrandTotal = () => {
    const baseAmount = exactMath.add(
      this.getSubtotal(this.showPrice),
      this.getHandlingFee(),
      // this.getShippingFee()
    );

    return exactMath.add(baseAmount, this.getTax());
  };

  public isFullyAllocated = () =>
    !this.items.find((item) => !item.asNewCartItem().isFullyAllocated());

  public isOverSpend(): boolean {
    const customerUser = this.session.$customerUser.value;

    if (!customerUser || !customerUser.isBlockOverSpend) {
      return false;
    }

    const isFullyAllocated = this.items.every(
      (item) =>
        !!item.allocationCollectionId && !!item.allocationCollectionConsumption,
    );

    if (isFullyAllocated) {
      return false;
    }

    const availableBalance =
      customerUser.userAllocations
        ?.filter((a) => a.type === "dollar")
        .reduce((sum, a) => sum + (a.balance ?? 0), 0) ?? 0;

    const allocatedTotal = this.getAllocatedGrandTotal();
    if (availableBalance <= 0 || allocatedTotal <= 0) {
      return true;
    }

    return allocatedTotal > availableBalance;
  }

  public getAllocatedGrandTotal = () => {
    const baseAmount = exactMath.add(
      this.getSubtotal(false),
      this.getHandlingFee(),
      // this.getShippingFee()
    );

    return exactMath.add(baseAmount, this.getTax());
  };

  /**
   * @description allows for the partial save of cart attributes
   *
   * @param props
   */
  public saveCartAttrs(customerId: number, props?: (keyof CartAttrs)[]) {
    let update: Partial<CartAttrs>;

    if (!props) update = this.cart.attrs;
    else {
      update = props.reduce((accumulator, value) => {
        accumulator[value] = this.cart.attrs[value];

        return accumulator;
      }, {});
    }

    return this.cartApi.saveCartAttrs(customerId, update);
  }

  public requestCCUpdate = () => {
    this.ccUpdateRequest$.next(null);
  };

  public handleCCUpdateRequests = () => {
    // Every time a ccUpdateRequest is recieved
    this.ccUpdateRequest$
      .pipe(
        // Prevent duplicate calls for 500ms
        debounce(() => interval(500)),
        // Do nothing if there is no cartId
        filter(() => !!this.cart.id),
        // Prepare the transaction
        switchMap(() => this.cartApi.prepareCCTransaction(this.cart.id)),
      )
      .subscribe((data) => {
        // Notify downstream handlers of the updated payment details
        this.cardPaymentDetails$.next(data);
      });
  };

  /**
   * @description Orders the current cart
   */
  public placeOrder = (customerId: number) => {
    return this.cartApi.placeOrder(customerId);
  };

  /**
   * @description Reset the cart and cart items stored in the service
   *
   * @return void
   */
  public clearCart() {
    this.cart = new Cart();
    this.items = [];
  }

  public completeCCTransaction(transactionResponse: {
    [key: string]: string;
  }): Observable<CartAttrs & HasId> {
    return apiCallWrapper(
      this.cartApi.completeCCTransaction(transactionResponse),
      {
        notificationsService: this.notifications,
        action: "Complete CC TRANSACTION",
      },
    );
  }

  public fetchChartData(query: IQueryFilter) {
    let params = queryToParams(query);
    return this.cartApi.fetchChartData(params);
  }

  public getLastAddress() {
    if (!this.session.$userData.value?.isAdmin) {
      return this.cartApi.getLastAddress();
    }

    return of();
  }

  public updateOrderShippingDetails(
    shipmentID: string | number,
    trackingDetail: string | null,
    trackingLink: string | null,
  ) {
    return this.cartApi.updateOrderShippingDetails(
      shipmentID,
      trackingDetail,
      trackingLink,
    );
  }

  public getCart(id: number): Observable<Cart | undefined> {
    return apiCallWrapper(this.cartApi.getCart(id), {
      notificationsService: this.notifications,
      action: "Loading Order from DB",
    });
  }

  public reOrder(id: number): Observable<Cart | undefined> {
    return apiCallWrapper(
      this.loaderService.blockWithLoadingOverlayRx(this.cartApi.reOrder(id)),
      {
        notificationsService: this.notifications,
        action: "Processing Order",
      },
    );
  }

  public postOrderFieldValues(model: {
    [x: number]: string | number;
  }[], id: number | string) {
    return this.cartApi.postOrderFieldValues(model, id);
  }

  public getFields(customerId: number | string) {
    return this.cartApi.getFields(customerId);
  }

  public downloadInvoice(cartId: number) {
    this.notifications.create(
      "Info",
      "Please don’t navigate from this page while we are downloading your sales order.",
      NotificationType.Warn,
      { timeOut: 5000, showProgressBar: true },
    );
    return apiCallWrapper(
      this.loaderService.blockWithLoadingOverlayRx(
        this.cartApi.downloadInvoice(cartId),
      ),
      {
        notificationsService: this.notifications,
        action: "Downloading your Sales order",
        successMessage: "Sales order Downloaded Successfully",
      },
    );
  }

  public downloadXeroInvoice(invoiceNumber: string) {
    this.notifications.create(
      "Info",
      "Please don’t navigate from this page while we are downloading your invoice.",
      NotificationType.Warn,
      { timeOut: 5000, showProgressBar: true },
    );
    return apiCallWrapper(
      this.loaderService.blockWithLoadingOverlayRx(
        this.cartApi.downloadXeroInvoice(invoiceNumber),
      ),
      {
        notificationsService: this.notifications,
        action: "Downloading your Invoice",
        successMessage: "Invoice Downloaded Successfully",
      },
    );
  }

  public fetchInvoiceNumber(cartId: number) {
    return apiCallWrapper(
      this.loaderService.blockWithLoadingOverlayRx(
        this.cartApi.fetchInvoiceNumber(cartId),
      ),
      {
        notificationsService: this.notifications,
        action: "Fetching All Invoice",
        successMessage: "Invoice Fetched Successfully",
      },
    );
  }

  public createPaymentintent(cartId: number) {
    return this.cartApi.createPaymentintent(cartId);
  }

  public updateShipmentStatus(id: string) {
    return apiCallWrapper(
      this.loaderService.blockWithLoadingOverlayRx(
        this.cartApi.updateOrderShippingStatus(id),
      ),
      {
        notificationsService: this.notifications,
        action: "Updating Order Status",
      },
    );
  }

  allOrderChart(): Observable<IOrderChartResponse> {
    return this.cartApi.allOrderChart();
  }

  customerBusinessUnitsChart(): Observable<IBusinessUnitChartResponse> {
    return this.cartApi.customerBusinessUnitsChart();
  }

  visitorChart(): Observable<IVisitorResponse> {
    return this.cartApi.visitorChart();
  }
}
